import classNames from "classnames";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { SuccessfulTradeResponse } from "hooks/useTradeExecution";
import { OrderType } from "hooks/useTradingForm";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { getDecimalPlaces } from "views/trading/utils";

// Response interface based on the example data
interface TradeResponseData {
  id: number;
  extId: string;
  securityCode: string;
  currency: string;
  units: number;
  unitPrice: string;
  feePercentage: number;
  feeAmount: number;
  total: string;
  status: string;
}

// Type guard function to validate response
function isTradeResponseData(data: unknown): data is TradeResponseData {
  if (!data || typeof data !== "object") return false;

  const response = data as Partial<TradeResponseData>;

  return (
    typeof response.extId === "string" &&
    typeof response.securityCode === "string" &&
    typeof response.currency === "string" &&
    typeof response.units === "number" &&
    typeof response.unitPrice === "string" &&
    typeof response.feePercentage === "number" &&
    typeof response.feeAmount === "number" &&
    typeof response.total === "string" &&
    typeof response.status === "string" &&
    (response.status === "executed" || response.status === "accepted")
  );
}

interface TransactionDetailsProps {
  txData: SuccessfulTradeResponse;
  type: "buy" | "sell";
  orderType?: OrderType;
  limitPrice?: string;
}

const TransactionDetails = ({
  txData,
  type,
  orderType,
  limitPrice,
}: TransactionDetailsProps) => {
  const { t } = useModifiedTranslation();

  const response = txData.response;

  // Type guard check - return null if response doesn't match expected structure
  if (!isTradeResponseData(response)) {
    return null;
  }

  return (
    <div className="flex flex-col p-1 m-4 sm:m-0 h-full bg-white sm:bg-success rounded-3xl shadow-sm sm:shadow-none">
      <div className="block sm:hidden py-3 text-2xl font-bold text-center text-kvarn-dark-green bg-gray-200 rounded-t-3xl">
        {type === "buy"
          ? t("tradingModal.typeBuy")
          : t("tradingModal.typeSell")}
      </div>
      <div className="flex flex-col gap-2 items-center sm:items-start py-3 sm:py-2 px-4 m-2 sm:m-0 text-xl sm:text-2xl font-bold text-center sm:text-left text-black sm:text-white bg-success sm:bg-opacity-100 rounded-3xl bg-opacity-15">
        <IoMdCheckmarkCircleOutline className="sm:hidden w-10 h-10 fill-success" />

        {response.status === "executed"
          ? t("tradingModal.orderExecuted")
          : t("tradingModal.orderAccepted")}
      </div>
      <div className="flex flex-col px-6 pt-3 pb-4 text-kvarn-dark-green bg-white rounded-b-3xl">
        <DetailsRowItem
          label={t("tradingModal.side")}
          value={
            type === "buy"
              ? t("tradingModal.typeBuy")
              : t("tradingModal.typeSell")
          }
        />

        <DetailsRowItem
          label={t("tradingModal.units")}
          value={`${response.units} ${response.securityCode.toUpperCase()}`}
        />

        <DetailsRowItem
          label={t("tradingModal.transactionRate")}
          value={`${Number(response.unitPrice).toFixed(
            getDecimalPlaces(response.unitPrice)
          )} ${response.currency.toUpperCase()}`}
        />

        <DetailsRowItem
          label={
            type === "buy"
              ? t("tradingModal.buyingPrice")
              : t("tradingModal.sellingPrice")
          }
          value={`${(
            Number(response.units) * Number(response.unitPrice)
          ).toFixed(
            getDecimalPlaces(response.unitPrice)
          )} ${response.currency.toUpperCase()}`}
        />

        {orderType === "Limit" && limitPrice && (
          <DetailsRowItem
            label={t("tradingModal.limitPrice")}
            value={`${Number(limitPrice).toFixed(
              getDecimalPlaces(limitPrice)
            )} ${response.currency.toUpperCase()}`}
          />
        )}

        <DetailsRowItem
          label={t("tradingModal.feePercentage")}
          value={`${(response.feePercentage * 100).toFixed(2)}%`}
        />

        <DetailsRowItem
          label={t("tradingModal.feeAmount")}
          value={`${response.feeAmount.toFixed(
            getDecimalPlaces(response.feeAmount)
          )} ${response.currency.toUpperCase()}`}
        />

        <DetailsRowItem
          label={t("tradingModal.total")}
          value={`${Number(response.total).toFixed(
            getDecimalPlaces(response.total)
          )} ${response.currency.toUpperCase()}`}
          borderless
        />
      </div>
    </div>
  );
};

const DetailsRowItem = ({
  label,
  value,
  borderless,
}: {
  label: string;
  value: string;
  borderless?: boolean;
}) => {
  return (
    <div
      className={classNames(
        "flex justify-between items-center py-3 text-lg",
        borderless ? "" : "border-b border-gray-300"
      )}
    >
      <div className="opacity-80">{label}</div>
      <div className="font-bold">{value}</div>
    </div>
  );
};
export default TransactionDetails;
